import { useState } from "react"
import { Button, Card, CardBody } from "reactstrap"
import SectionHeader from "components/Common/SectionHeader"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useAuth } from "hooks/authHook"
import LoanPdfPrintOptionsModal from "./LoanPdfPrintOptionsModal"

export const LOAN_PDF_ACTIONS = [
  {
    key: "bank-delivery-challan",
    label: "Bank Delivery Challan",
    permission: ACCESS.MONEY_RECEIPT_LOAN_PDF.BANK_DELIVERY_CHALLAN,
  },
  {
    key: "bank-bill",
    label: "Bank Bill",
    permission: ACCESS.MONEY_RECEIPT_LOAN_PDF.BANK_BILL,
  },
  {
    key: "customer-money-receipt",
    label: "Customer Money Receipt",
    permission: ACCESS.MONEY_RECEIPT_LOAN_PDF.CUSTOMER_MONEY_RECEIPT,
  },
  {
    key: "bank-money-receipt",
    label: "Bank Money Receipt",
    permission: ACCESS.MONEY_RECEIPT_LOAN_PDF.BANK_MONEY_RECEIPT,
  },
  {
    key: "bank-payorder-letter",
    label: "Bank Payorder Letter",
    permission: ACCESS.MONEY_RECEIPT_LOAN_PDF.BANK_PAYORDER_LETTER,
  },
]

const PrintActionButton = ({
  label,
  color = "light",
  onClick,
  disabled,
  loading = false,
  loadingLabel = "Generating...",
}) => (
  <Button
    color={color}
    className="money-receipt-print-button"
    onClick={onClick}
    disabled={disabled || loading}
  >
    <i className={`bx ${loading ? "bx-loader bx-spin" : "bx-printer"} me-1`} />
    {loading ? loadingLabel : label}
  </Button>
)

const MoneyReceiptLoanPdfActions = ({
  title = "Loan PDF Documents",
  groupButtonLabel = "Generate Group PDF",
  emptyMessage = "Loan information required before generating loan PDFs.",
  icon = "bx bx-printer",
  hasLoanInformation,
  activeLoanPdfKey,
  onLoanPdfDownload,
  onLoanPdfGroupDownload,
  className = "",
}) => {
  const { hasAnyPermission } = useAuth()
  const [pendingAction, setPendingAction] = useState(null)

  const visibleActions = LOAN_PDF_ACTIONS.filter(action =>
    hasAnyPermission([action.permission])
  )

  if (
    !hasAnyPermission([ACCESS.MONEY_RECEIPT_LOAN.VIEW]) ||
    visibleActions.length === 0
  ) {
    return null
  }

  const openModalForSingle = key =>
    setPendingAction({ type: "single", key, label: labelForKey(key) })

  const openModalForGroup = () =>
    setPendingAction({ type: "group", label: groupButtonLabel })

  const closeModal = () => setPendingAction(null)

  const handleSubmitOptions = async options => {
    if (!pendingAction) return
    const action = pendingAction
    setPendingAction(null)
    if (action.type === "single") {
      await onLoanPdfDownload(action.key, options)
    } else {
      await onLoanPdfGroupDownload(options)
    }
  }

  return (
    <Card className={`border-0 shadow-sm ${className}`.trim()}>
      <CardBody className="p-3">
        <SectionHeader
          icon={icon}
          title={title}
          action={
            <Button
              color="success"
              size="sm"
              onClick={openModalForGroup}
              disabled={!hasLoanInformation || activeLoanPdfKey !== null}
            >
              <i
                className={`bx ${
                  activeLoanPdfKey === "group"
                    ? "bx-loader bx-spin"
                    : "bx-layer"
                } me-1`}
              />
              {activeLoanPdfKey === "group"
                ? "Generating..."
                : groupButtonLabel}
            </Button>
          }
        />

        <div className="money-receipt-print-grid">
          {visibleActions.map(action => (
            <PrintActionButton
              key={action.key}
              label={action.label}
              onClick={() => openModalForSingle(action.key)}
              disabled={!hasLoanInformation || activeLoanPdfKey !== null}
              loading={activeLoanPdfKey === action.key}
            />
          ))}
        </div>

        {!hasLoanInformation ? (
          <div className="text-muted small mt-3">{emptyMessage}</div>
        ) : null}
      </CardBody>

      <LoanPdfPrintOptionsModal
        isOpen={pendingAction !== null}
        toggle={closeModal}
        onSubmit={handleSubmitOptions}
        isSubmitting={activeLoanPdfKey !== null}
        title={
          pendingAction
            ? `Print Options - ${pendingAction.label}`
            : "Print Options"
        }
      />
    </Card>
  )
}

const labelForKey = key =>
  LOAN_PDF_ACTIONS.find(action => action.key === key)?.label || key

export default MoneyReceiptLoanPdfActions
